import React, { useEffect, createElement } from 'react';
import { DatabaseIcon, GlobeIcon, BrainCircuitIcon, LineChartIcon, ShieldCheckIcon, FileTextIcon, TrendingUpIcon, AlertTriangleIcon, UsersIcon, DownloadIcon, CheckCircleIcon, ArrowRightIcon, ChevronRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function SuperPlatformAI() {
  // Set page-specific meta tags
  useEffect(() => {
    // Store original values for cleanup
    const originalTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const ogType = document.querySelector('meta[property="og:type"]');
    const ogImage = document.querySelector('meta[property="og:image"]');
    const ogImageAlt = document.querySelector('meta[property="og:image:alt"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    const twitterImageAlt = document.querySelector('meta[name="twitter:image:alt"]');
    const canonical = document.querySelector('link[rel="canonical"]');
    const origDesc = metaDescription?.getAttribute('content') || '';
    const origKeywords = metaKeywords?.getAttribute('content') || '';
    const origOgTitle = ogTitle?.getAttribute('content') || '';
    const origOgDesc = ogDescription?.getAttribute('content') || '';
    const origOgUrl = ogUrl?.getAttribute('content') || '';
    const origOgType = ogType?.getAttribute('content') || '';
    const origOgImage = ogImage?.getAttribute('content') || '';
    const origOgImageAlt = ogImageAlt?.getAttribute('content') || '';
    const origTwitterTitle = twitterTitle?.getAttribute('content') || '';
    const origTwitterDesc = twitterDescription?.getAttribute('content') || '';
    const origTwitterImage = twitterImage?.getAttribute('content') || '';
    const origTwitterImageAlt = twitterImageAlt?.getAttribute('content') || '';
    const origCanonical = canonical?.getAttribute('href') || '';
    // Update title (Yoast format: Primary Keyword - Secondary | Brand)
    document.title = 'Super Platform AI - AI-Powered Strategic Decision Making | COSMO IMAGE AVIATION';
    // Update meta description (max 160 chars, keyword-rich)
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Super Platform AI: Advanced AI-powered data management and analytics platform for strategic decision-making. Integrated web crawler, document management, and real-time executive dashboard for government institutions.');
    }
    // Update keywords
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Super Platform AI, AI analytics platform, strategic decision making, data management, web crawler, executive dashboard, government technology, AI-powered insights, real-time analytics, COSMO IMAGE AVIATION');
    }
    // Update Open Graph tags
    if (ogTitle) ogTitle.setAttribute('content', 'Super Platform AI - AI-Powered Strategic Decision Making Platform');
    if (ogDescription) ogDescription.setAttribute('content', 'Advanced AI-powered data management and analytics platform integrating web crawling, document management, and real-time executive dashboard for strategic government decision-making.');
    if (ogUrl) ogUrl.setAttribute('content', 'https://www.cosmoimageaviation.com/super-platform-ai');
    if (ogType) ogType.setAttribute('content', 'product');
    if (ogImage) ogImage.setAttribute('content', 'https://uploadthingy.s3.us-west-1.amazonaws.com/fBfxqkLa5b2GcRB3ZnM6Eo/Screenshot_2025-11-11_143200.png');
    if (ogImageAlt) ogImageAlt.setAttribute('content', 'Super Platform AI Dashboard Interface');
    // Update Twitter Card tags
    if (twitterTitle) twitterTitle.setAttribute('content', 'Super Platform AI - AI-Powered Strategic Decision Making');
    if (twitterDescription) twitterDescription.setAttribute('content', 'Advanced AI-powered data management and analytics platform for strategic government decision-making with real-time executive dashboard.');
    if (twitterImage) twitterImage.setAttribute('content', 'https://uploadthingy.s3.us-west-1.amazonaws.com/fBfxqkLa5b2GcRB3ZnM6Eo/Screenshot_2025-11-11_143200.png');
    if (twitterImageAlt) twitterImageAlt.setAttribute('content', 'Super Platform AI Dashboard Interface');
    // Update canonical
    if (canonical) canonical.setAttribute('href', 'https://www.cosmoimageaviation.com/super-platform-ai');
    // Update og:image dimensions
    const ogImageWidth = document.querySelector('meta[property="og:image:width"]');
    const ogImageHeight = document.querySelector('meta[property="og:image:height"]');
    const origOgImageWidth = ogImageWidth?.getAttribute('content') || '';
    const origOgImageHeight = ogImageHeight?.getAttribute('content') || '';
    if (ogImageWidth) ogImageWidth.setAttribute('content', '1920');
    if (ogImageHeight) ogImageHeight.setAttribute('content', '1080');
    // Add page-specific JSON-LD structured data
    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.id = 'super-platform-ai-jsonld';
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Super Platform AI',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: 'AI-Powered Data Management and Analytics Platform for Strategic Decision Making. Integrates web crawling, document management, API integration, and real-time executive dashboard.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceCurrency: 'USD',
        seller: {
          '@type': 'Organization',
          name: 'COSMO IMAGE AVIATION PTE. LTD.',
          url: 'https://www.cosmoimageaviation.com/'
        }
      },
      featureList: 'AI-Driven Prioritization, Real-Time Executive Dashboard, Web & Media Crawler, Document Management, API & Database Integration, Social Media Analysis',
      screenshot: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fBfxqkLa5b2GcRB3ZnM6Eo/Screenshot_2025-11-11_143200.png',
      provider: {
        '@type': 'Organization',
        name: 'COSMO IMAGE AVIATION PTE. LTD.',
        url: 'https://www.cosmoimageaviation.com/'
      }
    });
    document.head.appendChild(jsonLd);
    // Add BreadcrumbList JSON-LD
    const breadcrumbLd = document.createElement('script');
    breadcrumbLd.type = 'application/ld+json';
    breadcrumbLd.id = 'super-platform-ai-breadcrumb';
    breadcrumbLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.cosmoimageaviation.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Products',
        item: 'https://www.cosmoimageaviation.com/#services'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: 'Super Platform AI',
        item: 'https://www.cosmoimageaviation.com/super-platform-ai'
      }]
    });
    document.head.appendChild(breadcrumbLd);
    // Scroll to top on mount
    window.scrollTo(0, 0);
    // Cleanup on unmount - restore all original values
    return () => {
      document.title = originalTitle;
      if (metaDescription) metaDescription.setAttribute('content', origDesc);
      if (metaKeywords) metaKeywords.setAttribute('content', origKeywords);
      if (ogTitle) ogTitle.setAttribute('content', origOgTitle);
      if (ogDescription) ogDescription.setAttribute('content', origOgDesc);
      if (ogUrl) ogUrl.setAttribute('content', origOgUrl);
      if (ogType) ogType.setAttribute('content', origOgType);
      if (ogImage) ogImage.setAttribute('content', origOgImage);
      if (ogImageAlt) ogImageAlt.setAttribute('content', origOgImageAlt);
      if (ogImageWidth) ogImageWidth.setAttribute('content', origOgImageWidth);
      if (ogImageHeight) ogImageHeight.setAttribute('content', origOgImageHeight);
      if (twitterTitle) twitterTitle.setAttribute('content', origTwitterTitle);
      if (twitterDescription) twitterDescription.setAttribute('content', origTwitterDesc);
      if (twitterImage) twitterImage.setAttribute('content', origTwitterImage);
      if (twitterImageAlt) twitterImageAlt.setAttribute('content', origTwitterImageAlt);
      if (canonical) canonical.setAttribute('href', origCanonical);
      // Remove page-specific JSON-LD
      document.getElementById('super-platform-ai-jsonld')?.remove();
      document.getElementById('super-platform-ai-breadcrumb')?.remove();
    };
  }, []);
  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen w-full bg-white font-sans" data-id="element-454">
      <Header data-id="element-455" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pb-32 px-4 bg-navy overflow-hidden isolate" data-id="element-456">
        {/* Background Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-teal/20 z-0" data-id="element-457"></div>

        {/* Pattern Overlay */}
        <div className="absolute inset-0 opacity-10 z-0" data-id="element-458">
          <div className="absolute inset-0" style={{
          backgroundImage: 'url(\'data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\')'
        }} data-id="element-459"></div>
        </div>

        <div className="container mx-auto max-w-[var(--maxw)] relative z-10" data-id="element-460">
          <div className="max-w-4xl" data-id="element-461">
            <div className="inline-block border-l-4 border-teal pl-4 mb-6" data-id="element-462">
              <span className="text-teal font-bold tracking-widest uppercase text-sm" data-id="element-463">
                AI PLATFORM
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight" data-id="element-464">
              Super Platform AI
            </h1>
            <p className="text-xl md:text-2xl mb-10 max-w-3xl text-gray-200 font-light leading-relaxed" data-id="element-465">
              AI-Powered Data Management and Analytics Platform for Strategic
              Decision Making
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-466">
              <button onClick={() => scrollToSection('features')} className="bg-teal hover:bg-teal/90 text-white font-bold py-4 px-8 rounded-none transition-all flex items-center justify-center gap-2 group" data-id="element-467">
                View Features
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-468" />
              </button>
              <a href="https://drive.google.com/file/d/1gYyQ_UrtRIl6V5GrMtJMq9ink_NMuEgB/view?pli=1" target="_blank" rel="noopener noreferrer" className="bg-white hover:bg-gray-100 text-navy font-bold py-4 px-8 rounded-none transition-all flex items-center justify-center gap-2" data-id="element-469">
                <DownloadIcon className="w-5 h-5" data-id="element-470" />
                Download Brochure
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16" data-id="element-471">
            <div className="bg-white p-8 border-l-4 border-teal shadow-lg" data-id="element-472">
              <BrainCircuitIcon className="w-10 h-10 text-teal mb-4" data-id="element-473" />
              <h3 className="text-xl font-bold text-navy mb-2" data-id="element-474">AI-Driven</h3>
              <p className="text-gray-600" data-id="element-475">
                Automatic prioritization powered by artificial intelligence
              </p>
            </div>
            <div className="bg-white p-8 border-l-4 border-teal shadow-lg" data-id="element-476">
              <LineChartIcon className="w-10 h-10 text-teal mb-4" data-id="element-477" />
              <h3 className="text-xl font-bold text-navy mb-2" data-id="element-478">Real-Time</h3>
              <p className="text-gray-600" data-id="element-479">
                Executive dashboard with up-to-date data
              </p>
            </div>
            <div className="bg-white p-8 border-l-4 border-teal shadow-lg" data-id="element-480">
              <ShieldCheckIcon className="w-10 h-10 text-teal mb-4" data-id="element-481" />
              <h3 className="text-xl font-bold text-navy mb-2" data-id="element-482">Strategic</h3>
              <p className="text-gray-600" data-id="element-483">
                Designed for high-level government institutions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Preview Section */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-484">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-485">
          <div className="text-center mb-16" data-id="element-486">
            <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-487">
              INTERFACE
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-navy mb-6" data-id="element-488">
              Platform Interface
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-489">
              Experience a powerful, intuitive dashboard designed for strategic
              decision-making with real-time data visualization and AI-powered
              insights.
            </p>
          </div>

          <div className="bg-white p-2 rounded-none shadow-2xl border border-gray-100" data-id="element-490">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fBfxqkLa5b2GcRB3ZnM6Eo/Screenshot_2025-11-11_143200.png" alt="Super Platform AI Dashboard Interface" className="w-full h-auto" data-id="element-491" />
          </div>
        </div>
      </section>

      {/* Core Concept */}
      <section id="concept" className="py-20 lg:py-24 bg-gray-50" data-id="element-492">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-493">
          <div className="text-center mb-16" data-id="element-494">
            <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-495">
              OVERVIEW
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-navy mb-6" data-id="element-496">
              Core Concept
            </h2>
          </div>

          <div className="bg-white p-10 border-l-4 border-navy shadow-sm" data-id="element-497">
            <p className="text-xl mb-10 text-navy font-medium" data-id="element-498">
              Super Platform AI is an AI-powered data management and analytics
              platform that integrates:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10" data-id="element-499">
              <div className="flex items-start gap-4" data-id="element-500">
                <div className="bg-teal/10 p-3 rounded-none" data-id="element-501">
                  <GlobeIcon className="w-6 h-6 text-teal" data-id="element-502" />
                </div>
                <div data-id="element-503">
                  <h3 className="font-bold text-xl mb-2 text-navy" data-id="element-504">
                    Web & Media Crawler
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-505">
                    Automated data collection from various online sources
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-506">
                <div className="bg-teal/10 p-3 rounded-none" data-id="element-507">
                  <FileTextIcon className="w-6 h-6 text-teal" data-id="element-508" />
                </div>
                <div data-id="element-509">
                  <h3 className="font-bold text-xl mb-2 text-navy" data-id="element-510">
                    Document Management
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-511">
                    Integrated management of strategic document files
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-512">
                <div className="bg-teal/10 p-3 rounded-none" data-id="element-513">
                  <DatabaseIcon className="w-6 h-6 text-teal" data-id="element-514" />
                </div>
                <div data-id="element-515">
                  <h3 className="font-bold text-xl mb-2 text-navy" data-id="element-516">
                    API & Database Integration
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-517">
                    Synchronization with internal institutional systems
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-518">
                <div className="bg-teal/10 p-3 rounded-none" data-id="element-519">
                  <LineChartIcon className="w-6 h-6 text-teal" data-id="element-520" />
                </div>
                <div data-id="element-521">
                  <h3 className="font-bold text-xl mb-2 text-navy" data-id="element-522">
                    Real-Time Dashboard
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-523">
                    Visualization of priorities and executive insights
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-6 border-l-4 border-teal" data-id="element-524">
              <p className="font-bold text-lg mb-2 text-navy" data-id="element-525">
                Platform Focus:
              </p>
              <p className="text-gray-700" data-id="element-526">
                Transform scattered data into structured, prioritized, and
                actionable insights for strategic decision-making.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Flow */}
      <section id="flow" className="py-20 lg:py-24 bg-white" data-id="element-527">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-528">
          <div className="text-center mb-16" data-id="element-529">
            <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-530">
              PROCESS
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-navy mb-6" data-id="element-531">
              Product Flow
            </h2>
          </div>

          <div className="space-y-8" data-id="element-532">
            {[{
            title: '1. Data Collection',
            icon: GlobeIcon,
            items: ['Web crawler (news portals, online media, official websites)', 'Social media & public conversations (via API)', 'Internal file upload: PDF, JPEG, Excel, report documents', 'API & agency database integration']
          }, {
            title: '2. Data Management & Synchronization',
            icon: DatabaseIcon,
            items: ['Automatic normalization & categorization', 'Issue tagging: security, politics, economy, social, capital city, energy, disinformation', 'Structured storage in AI Priority Database']
          }, {
            title: '3. Prioritization Engine',
            icon: BrainCircuitIcon,
            items: ['Automatic priority recommendations from AI', 'Manual adjustment by analysts', 'Personalized priorities per institution / per leader']
          }, {
            title: '4. Analytics & Dashboard',
            icon: LineChartIcon,
            items: ['Real-time Executive Dashboard', 'Executive summary, trends, issue escalation', 'Data-driven policy recommendations', 'Output can be exported as official reports']
          }].map((step, index) => <div key={index} className="bg-white p-8 border-l-4 border-teal shadow-sm hover:shadow-md transition-shadow" data-id="element-533">
                <div className="flex items-start gap-6" data-id="element-534">
                  <div className="bg-navy text-white p-4 rounded-none" data-id="element-535">
                    <step.icon className="w-8 h-8" data-id="element-536" />
                  </div>
                  <div className="flex-1" data-id="element-537">
                    <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-538">
                      {step.title}
                    </h3>
                    <ul className="space-y-3" data-id="element-539">
                      {step.items.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-540">
                          <ChevronRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" data-id="element-541" />
                          <span className="text-gray-700 text-lg" data-id="element-542">{item}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section id="features" className="py-20 lg:py-24 bg-gray-50" data-id="element-543">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-544">
          <div className="text-center mb-16" data-id="element-545">
            <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-546">
              CAPABILITIES
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-navy mb-6" data-id="element-547">
              Core Features
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-548">
            <div className="bg-white p-10 border-l-4 border-teal shadow-sm" data-id="element-549">
              <div className="bg-navy text-white p-3 w-fit mb-6" data-id="element-550">
                <FileTextIcon className="w-8 h-8" data-id="element-551" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-6" data-id="element-552">
                A. Data Management
              </h3>
              <ul className="space-y-4" data-id="element-553">
                <li className="flex items-start gap-3" data-id="element-554">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-555" />
                  <span className="text-gray-700 text-lg" data-id="element-556">
                    Multi-format upload (.pdf, .jpeg, .xlsx, .docx, etc.)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-557">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-558" />
                  <span className="text-gray-700 text-lg" data-id="element-559">
                    Management of strategic data archives in one integrated
                    platform
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-560">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-561" />
                  <span className="text-gray-700 text-lg" data-id="element-562">
                    AI-powered fast search (keywords, entities, issues)
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-10 border-l-4 border-teal shadow-sm" data-id="element-563">
              <div className="bg-navy text-white p-3 w-fit mb-6" data-id="element-564">
                <GlobeIcon className="w-8 h-8" data-id="element-565" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-6" data-id="element-566">
                B. Web Crawler & Integration
              </h3>
              <ul className="space-y-4" data-id="element-567">
                <li className="flex items-start gap-3" data-id="element-568">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-569" />
                  <span className="text-gray-700 text-lg" data-id="element-570">
                    Automatic scraping from various news sites & official
                    sources
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-571">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-572" />
                  <span className="text-gray-700 text-lg" data-id="element-573">
                    Simultaneous multi-scraper for speed and broad coverage
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-574">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-575" />
                  <span className="text-gray-700 text-lg" data-id="element-576">
                    API & internal agency database integration
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-10 border-l-4 border-teal shadow-sm" data-id="element-577">
              <div className="bg-navy text-white p-3 w-fit mb-6" data-id="element-578">
                <TrendingUpIcon className="w-8 h-8" data-id="element-579" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-6" data-id="element-580">
                C. AI Priority Processing List
              </h3>
              <ul className="space-y-4" data-id="element-581">
                <li className="flex items-start gap-3" data-id="element-582">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-583" />
                  <span className="text-gray-700 text-lg" data-id="element-584">
                    <strong className="text-navy" data-id="element-585">AI-Driven Priority:</strong>{' '}
                    system recommends priority issues based on volume, tone,
                    risk, and trends
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-586">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-587" />
                  <span className="text-gray-700 text-lg" data-id="element-588">
                    <strong className="text-navy" data-id="element-589">Manual Override:</strong>{' '}
                    analysts can change priorities according to policy needs
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-590">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-591" />
                  <span className="text-gray-700 text-lg" data-id="element-592">
                    <strong className="text-navy" data-id="element-593">Persona-based View:</strong>{' '}
                    priorities customized for national leaders, intelligence
                    units, government public relations, and specific issue desks
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-10 border-l-4 border-teal shadow-sm" data-id="element-594">
              <div className="bg-navy text-white p-3 w-fit mb-6" data-id="element-595">
                <LineChartIcon className="w-8 h-8" data-id="element-596" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-6" data-id="element-597">
                D. Executive Dashboard
              </h3>
              <ul className="space-y-4" data-id="element-598">
                <li className="flex items-start gap-3" data-id="element-599">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-600" />
                  <span className="text-gray-700 text-lg" data-id="element-601">
                    <strong className="text-navy" data-id="element-602">
                      Real-Time AI Analysis:
                    </strong>{' '}
                    issue summary, escalation & potential crisis detection,
                    policy recommendations
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-603">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-604" />
                  <span className="text-gray-700 text-lg" data-id="element-605">
                    <strong className="text-navy" data-id="element-606">
                      Social Media Analysis AI:
                    </strong>{' '}
                    public sentiment mapping, hoax & disinformation detection
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-607">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-608" />
                  <span className="text-gray-700 text-lg" data-id="element-609">
                    <strong className="text-navy" data-id="element-610">
                      Dynamic Priority & Attention:
                    </strong>{' '}
                    analytical focus that can be adjusted to current context
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-611">
                  <ArrowRightIcon className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-612" />
                  <span className="text-gray-700 text-lg" data-id="element-613">
                    <strong className="text-navy" data-id="element-614">Real-Time Collection:</strong>{' '}
                    continuous synchronization from all data sources
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Value */}
      <section id="value" className="py-20 lg:py-24 bg-white" data-id="element-615">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-616">
          <div className="text-center mb-16" data-id="element-617">
            <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-618">
              BENEFITS
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-navy mb-6" data-id="element-619">
              Strategic Value
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-620">
            {[{
            icon: AlertTriangleIcon,
            title: 'Reduce Information Overload',
            description: 'Data is directly presented as insights, not just raw data'
          }, {
            icon: TrendingUpIcon,
            title: 'Fast Decision Making',
            description: 'Support decisions in dynamic national environments (security, politics, economy, cyber, propaganda)'
          }, {
            icon: ShieldCheckIcon,
            title: 'Single Source of Truth',
            description: 'Become a trusted cross-agency analytical center'
          }, {
            icon: UsersIcon,
            title: 'Full Customization',
            description: 'Can be adapted to the structure and SOPs of government institutions'
          }, {
            icon: BrainCircuitIcon,
            title: 'AI-Powered Insights',
            description: 'Deep analysis based on artificial intelligence for accurate insights'
          }, {
            icon: DatabaseIcon,
            title: 'Comprehensive Integration',
            description: 'Connect all strategic data sources in one platform'
          }].map((value, index) => <div key={index} className="bg-gray-50 p-8 border-l-4 border-transparent hover:border-teal transition-all duration-300 shadow-sm hover:shadow-md" data-id="element-621">
                <div className="text-teal mb-6" data-id="element-622">
                  <value.icon className="w-10 h-10" data-id="element-623" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-4" data-id="element-624">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-625">
                  {value.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-20 lg:py-24 bg-navy text-white relative overflow-hidden" data-id="element-626">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-sky to-navy opacity-50" data-id="element-627"></div>
        <div className="container mx-auto max-w-4xl text-center relative z-10" data-id="element-628">
          <UsersIcon className="w-16 h-16 mx-auto mb-8 text-teal" data-id="element-629" />
          <h2 className="text-3xl md:text-5xl font-bold mb-8" data-id="element-630">
            Primary Target
          </h2>
          <p className="text-xl md:text-2xl mb-12 font-light text-gray-200" data-id="element-631">
            Specifically designed for high-level government institutions,
            ministries/agencies, and strategic analytical units
          </p>
          <div className="bg-white/10 backdrop-blur-md p-10 border-l-4 border-teal" data-id="element-632">
            <p className="text-xl text-white" data-id="element-633">
              Examples: National Defense Council, Ministries, Intelligence
              Agencies, and Government Strategic Units
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-24 bg-gray-50 relative" data-id="element-634">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-navy via-teal to-navy" data-id="element-635"></div>
        <div className="container mx-auto max-w-4xl text-center px-4" data-id="element-636">
          <h2 className="text-3xl md:text-5xl font-bold text-navy mb-8" data-id="element-637">
            Ready to Optimize Strategic Decision Making?
          </h2>
          <p className="text-xl text-gray-600 mb-12" data-id="element-638">
            Contact us for a demo and further consultation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-639">
            <a href="mailto:contact@cosmoimageaviation.co" className="bg-teal hover:bg-teal/90 text-white font-bold py-4 px-10 rounded-none transition-all shadow-lg" data-id="element-640">
              Contact Us
            </a>
            <a href="https://drive.google.com/file/d/1gYyQ_UrtRIl6V5GrMtJMq9ink_NMuEgB/view?pli=1" target="_blank" rel="noopener noreferrer" className="bg-white border-2 border-navy hover:bg-navy hover:text-white text-navy font-bold py-4 px-10 rounded-none transition-all inline-flex items-center justify-center gap-2" data-id="element-641">
              <DownloadIcon className="w-5 h-5" data-id="element-642" />
              Download Brochure
            </a>
          </div>
        </div>
      </section>

      <Footer data-id="element-643" />
    </div>;
}